import { ClockDirection, Game } from "../types/models";

/**
 * Reading a game clock.
 *
 * The API stores a clock as a reading plus the instant that reading was true,
 * and hands clients `clock_display_seconds` — what it read when the response
 * was built. A client ticks on from that, measuring elapsed time with its own
 * stopwatch rather than comparing the server's timestamp against its own idea
 * of the time, so a device whose clock is wrong still shows what everyone else
 * shows.
 */

/** The clock fields a client may write, as PATCH /games/:id takes them. */
export type ClockUpdate = Partial<{
  current_period: number;
  clock_seconds: number;
  clock_limit_seconds: number | null;
  clock_direction: ClockDirection;
  clock_running: boolean;
}>;

/** The clock fields a display needs, lifted off a game. */
export interface ClockReading {
  /** What the clock read when the response was built. */
  displaySeconds: number;
  running: boolean;
  direction: ClockDirection;
  /** The period's length, or null when none is set. */
  limitSeconds: number | null;
}

export function clockReadingOf(game: Game): ClockReading {
  return {
    displaySeconds: game.clock_display_seconds,
    running: game.clock_running,
    direction: game.clock_direction,
    limitSeconds: game.clock_limit_seconds,
  };
}

/** Hold the clock inside the period: never below zero, never past its length. */
function clamp(seconds: number, limitSeconds: number | null): number {
  const bounded = Math.max(seconds, 0);
  return limitSeconds === null ? bounded : Math.min(bounded, limitSeconds);
}

/**
 * The clock's reading `elapsedSeconds` after the server's reading was taken.
 * A stopped clock reads the same whatever has elapsed.
 */
export function readClock(clock: ClockReading, elapsedSeconds: number): number {
  if (!clock.running) {
    return clamp(clock.displaySeconds, clock.limitSeconds);
  }

  const elapsed = Math.max(elapsedSeconds, 0);
  const raw =
    clock.direction === "up"
      ? clock.displaySeconds + elapsed
      : clock.displaySeconds - elapsed;

  return clamp(raw, clock.limitSeconds);
}

/**
 * Whether the clock has run out the period. A count-up with no length set has
 * no end to reach, so it never finishes.
 */
export function isClockFinished(clock: ClockReading, seconds: number): boolean {
  if (clock.direction === "down") return seconds <= 0;
  return clock.limitSeconds !== null && seconds >= clock.limitSeconds;
}

/** M:SS, the way a scoreboard shows it — 20:00, 7:04, 0:09. */
export function formatClock(totalSeconds: number): string {
  const safe = Math.max(Math.floor(totalSeconds), 0);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** The longest a period can be set to, matching the API's per-period cap. */
export const MAX_PERIOD_MINUTES = 60;

/**
 * How many periods a league plays before overtime, when the league itself is
 * still loading. Three is what all but a handful of leagues play.
 */
const DEFAULT_REGULATION_PERIODS = 3;

export function regulationPeriodsOf(
  periodMinutes: number[] | undefined | null,
): number {
  return periodMinutes && periodMinutes.length > 0
    ? periodMinutes.length
    : DEFAULT_REGULATION_PERIODS;
}

/**
 * The length the next period should start at: what the league set for it, or —
 * for overtime, which leagues do not set a length for — the length of the
 * period just played.
 */
export function periodLengthSeconds(
  period: number,
  periodMinutes: number[] | undefined | null,
  fallbackSeconds: number | null,
): number | null {
  const minutes = periodMinutes?.[period - 1];
  if (typeof minutes === "number" && minutes > 0) return minutes * 60;
  return fallbackSeconds;
}

/**
 * "Period 2", or "OT" and "2OT" for the periods past the ones the league plays
 * in regulation.
 */
export function periodLabel(period: number, regulationPeriods: number): string {
  if (period <= regulationPeriods) return `Period ${period}`;

  const overtime = period - regulationPeriods;
  return overtime === 1 ? "OT" : `${overtime}OT`;
}

/** The same, short enough for a public scoreboard: "P2", "OT", "2OT". */
export function periodShortLabel(
  period: number,
  regulationPeriods: number,
): string {
  if (period <= regulationPeriods) return `P${period}`;

  const overtime = period - regulationPeriods;
  return overtime === 1 ? "OT" : `${overtime}OT`;
}
